import { Suspense, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { ErrorBoundary } from '@xing-com/crate-core-error-boundary';
import { useParams } from '@xing-com/crate-core-router';
import {
  getXingJobsDirectoryLetterJobRolePath,
  getXingJobsDirectoryLetterPath,
} from '@xing-com/crate-core-routes';
import { Head } from '@xing-com/crate-master-directory-head';
import { deslugify } from '@xing-com/crate-master-directory-helpers';
import {
  useGetPopularSkills,
  useGetTopLocations,
} from '@xing-com/crate-master-directory-hooks';
import { LinkList } from '@xing-com/crate-master-directory-links';
import { DirectoryList } from '@xing-com/crate-master-directory-list';
import { SearchHeader } from '@xing-com/crate-master-directory-search-header';
import { useHost } from '@xing-com/crate-xinglet';

import * as S from './job-role.styled';
import { getEmploymentTypeEntries } from './utils';

type Params = {
  letter: string;
  jobRole: string;
};

const TopLocationsList: React.FC<{ jobRole: string }> = ({ jobRole }) => {
  const { collection } = useGetTopLocations({
    jobRole,
  });

  return <LinkList entries={collection} />;
};

const PopularSkillsList: React.FC<{ jobRole: string }> = ({ jobRole }) => {
  const { collection } = useGetPopularSkills({
    jobRole,
  });

  return <LinkList entries={collection} showMore />;
};

export const MasterDirectoryJobRolePage: React.FC = () => {
  const { isPreview } = useHost();
  const externalUrl = isPreview
    ? 'https://preview.xing.com'
    : 'https://www.xing.com';
  const { formatMessage } = useIntl();
  const { letter, jobRole } = useParams<Params>();
  const { track } = useTracking();
  const jobRoleDeslugified = deslugify(jobRole);
  const description = formatMessage(
    { id: 'CRATE_MASTER_DIRECTORY_JOB_ROLE_DESCRIPTION' },
    { jobRole: jobRoleDeslugified }
  );
  const title = formatMessage(
    { id: 'CRATE_MASTER_DIRECTORY_JOB_ROLE_TITLE' },
    { jobRole: jobRoleDeslugified }
  );
  const path = getXingJobsDirectoryLetterJobRolePath({
    letter: letter.toLocaleLowerCase(),
    jobRole: jobRole.toLocaleLowerCase(),
  });
  const canonicalUrl = `${externalUrl}${path}`;
  const employmentTypeEntries = getEmploymentTypeEntries({
    jobRole,
    jobRoleDeslugified,
    formatMessage,
  });
  const breadcrumbItems = [
    {
      title: formatMessage({
        id: 'CRATE_MASTER_DIRECTORY_BREADCRUMB_HOME',
      }),
      url: getXingJobsDirectoryLetterPath({ letter: 'a' }),
    },
    {
      title: letter.toLocaleUpperCase(),
      url: getXingJobsDirectoryLetterPath({ letter }),
    },
    { title: jobRoleDeslugified, url: '' },
  ];

  useEffect(() => {
    track({
      application: 'wbm',
      eventSchema: 'basic',
      sentBy: 'directory',
      page: 'directory/job_role',
      event: 'viewed_screen',
      type: 'nwt',
    });
  }, [track]);

  return (
    <>
      <Head
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        robots="noodp"
      />

      <SearchHeader />

      <S.Container>
        <S.SeoBreadcrumb items={breadcrumbItems} />

        <S.Hero size="small" sizeConfined="medium">
          <FormattedMessage
            id="CRATE_MASTER_DIRECTORY_JOB_ROLE_HEADLINE"
            values={{
              jobRole: jobRoleDeslugified,
            }}
          />
        </S.Hero>

        <LinkList entries={employmentTypeEntries} />

        <S.Headline size="xlarge">
          <FormattedMessage
            id="CRATE_MASTER_DIRECTORY_JOB_ROLE_LOCATIONS_HEADLINE"
            values={{
              jobRole: jobRoleDeslugified,
            }}
          />
        </S.Headline>
        <ErrorBoundary onError={() => <LinkList entries={[]} />}>
          <Suspense fallback={<LinkList entries={[]} />}>
            <TopLocationsList jobRole={jobRole} />
          </Suspense>
        </ErrorBoundary>

        <S.Headline size="xlarge">
          <FormattedMessage
            id="CRATE_MASTER_DIRECTORY_JOB_ROLE_SKILLS_HEADLINE"
            values={{
              jobRole: jobRoleDeslugified,
            }}
          />
        </S.Headline>
        <ErrorBoundary onError={() => <LinkList entries={[]} showMore />}>
          <Suspense fallback={<LinkList entries={[]} showMore />}>
            <PopularSkillsList jobRole={jobRole} />
          </Suspense>
        </ErrorBoundary>

        <S.Headline size="xlarge">
          <FormattedMessage id="CRATE_MASTER_DIRECTORY_SEARCH_BY_LETTERS" />
        </S.Headline>
        <DirectoryList
          letter={letter}
          route={getXingJobsDirectoryLetterPath({ letter: '' })}
        />
      </S.Container>
    </>
  );
};
